<template>
  <div class="field" :class="{ required: required }" v-diff-field="diff">
    <label v-if="labelText" :for="uid">
      <b>{{ labelText | capitalize }}</b>
    </label>

    <p v-html="description" v-if="description"></p>

    <section
      class="ui fluid search selection dropdown"
      :class="{
        active: !hidden && focused,
        multiple: multiple,
        disabled: disabled
      }"
      @click="inputFocused"
      @mouseleave="debouncedHide"
      @mouseenter="cancelHide"
    >
      <i class="dropdown icon"></i>

      <div v-if="multiple">
        <div
          class="ui label"
          :style="[labelStyle(item)]"
          v-for="item in currentItemsUnique"
          :key="item.id"
        >
          <slot name="selected" :item="item">
            {{ item && item.name }}
          </slot>
          <i class="delete icon" @click="removeItem(item)"></i>
        </div>
      </div>

      <input
        v-if="allowSearch"
        class="search"
        type="text"
        autocomplete="off"
        :placeholder="placeholderText"
        :id="uid"
        v-model.trim="model"
        @focus="inputFocused()"
        @blur="inputBlurred()"
        @keyup.up.exact="activateItem('up')"
        @keyup.down.exact="activateItem('down')"
        @keydown.esc.exact="hideResults()"
        @keyup.exact="inputKeyup($event)"
        @keydown.enter.prevent.exact="selectItem(activeItem)"
        @keydown.delete.exact="removeLastItem()"
        :required="multiple ? required && !currentItems.length : required"
        :disabled="disabled"
        ref="inputField"
      />

      <template v-if="!allowSearch">
        <slot name="selected" v-if="selectedItem" :item="selectedItem">
          <span class="text">{{ selectItem && selectedItem.name }}</span>
        </slot>
        <div v-else>{{ placeholderText }}</div>
      </template>

      <nav class="menu transition" :class="{ visible: focused && !hidden }" ref="typeaheadMenu">
        <article
          class="item"
          :class="{
            selected: activeItem && activeItem.id === item.id,
            noSelection: item.id === noSelectionItem.id
          }"
          @mouseenter="hover(item)"
          @mouseleave="hover()"
          @mousedown.prevent="selectItem(item)"
          v-for="item in displayedItems"
          :key="item.id"
        >
          <slot name="dropdown" :item="item">
            <span class="text">{{ item && item.name }}</span>
            <span class="description" v-if="item && item.description">{{ item.description }}</span>
          </slot>
        </article>
        <article
          class="item disabled"
          v-if="(shouldFilterItems && !filteredItems.length) || !displayedItems.length"
        >
          <p>{{ emptyText }}</p>
        </article>
      </nav>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
.search {
  margin-bottom: 1em;
}

.noSelection {
  font-style: italic;
}

.ui.dropdown {
  .ui.label {
    margin-right: 4px;
    margin-left: 0px;
    margin-top: 2px;
    margin-bottom: 2px;
  }
}
</style>

<script>
// @flow
/**
 * Typeahead component.
 *
 * Provides means to filter through provided `items`.
 * Supports multiple-selection via the `multiple` prop.
 */
type Item = {
  name: string,
  id: string
}

import Vue from 'vue'
import fuzzy from 'fuzzy'
import uuid from 'uuid'
import _ from 'lodash'
import tinycolor from 'tinycolor2'

export default {
  name: 'typeahead',
  props: {
    placeholderText: null,
    emptyText: {
      type: String,
      default: () => null
    },
    labelText: null,
    description: null,
    items: {
      type: Array, // Items to select from.
      validator: (val: any) => _.isNil(_.find(val, v => _.isNil(v.name) && _.isNil(cur.id)))
    },
    maxItems: {
      type: Number,
      default: () => 10
    },
    required: {
      type: Boolean,
      default: () => false
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    multiple: {
      type: Boolean,
      default: () => false
    },
    shouldFilterItems: {
      type: Boolean,
      default: () => true
    },
    singleItemFormatter: {
      type: Function,
      default: (v: any) => v?.name
    },
    matchExtractor: {
      type: Function,
      default: (v: any) => v.name
    },
    selectHandler: {
      type: Function,
      default: () => () => {}
    },
    removeHandler: {
      type: Function,
      default: () => () => {}
    },
    createHandler: {
      type: Function,
      default: () => () => null
    },
    currentItems: {
      type: null, // Optional, only for 'multiple'.
      default: () => []
    },
    currentItem: {
      type: String, // Optional, only for non-`multiple`.
      default: () => ''
    },
    labelStyle: {
      type: Function,
      default: (v: any) => {}
    },
    allowSearch: {
      type: Boolean,
      default: () => true
    },
    diff: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    filteredItems() {
      const fuzzyFilterOptions = {
        extract: this.matchExtractor
      }

      return fuzzy
        .filter(this.searchTerm, this.nonSelectedItems, fuzzyFilterOptions)
        .slice(0, this.maxItems)
        .map(item => item.original)
    },

    nonSelectedItems() {
      const currentItemIds = this.currentItems.map(i => i.id)
      return _.filter(this.items, i => !_.includes(currentItemIds, i.id))
    },

    displayedItems() {
      let displayedItems = []

      if (this.shouldFilterItems) {
        displayedItems = this.filteredItems.slice()
      } else {
        displayedItems = this.orderItemsByModel()
      }

      if (displayedItems.length && !this.required && !this.multiple) {
        displayedItems.unshift(this.noSelectionItem)
      }

      return displayedItems
    },
    currentItemsUnique() {
      return _.uniqBy(this.currentItems, 'id')
    }
  },

  data() {
    return {
      uid: `typeahead${uuid.v4()}`,
      activePosition: 0,
      activeItem: null,
      focused: false,
      hidden: false,
      model:
        this.singleItemFormatter && !_.isString(this.currentItem)
          ? this.singleItemFormatter(this.currentItem)
          : this.currentItem,
      selectedItem: null,
      searchTerm: '',
      debouncedHide: _.debounce(this.inputBlurred, 500),
      noSelectionItem: { id: '', name: this.emptyText || this.$i18n.t('NO_SELECTION') }
    }
  },

  methods: {
    cancelHide() {
      this.debouncedHide.cancel()
    },
    hover(item: any) {
      this.$emit('hover', item)
    },
    inputBlurred() {
      // Does it still have focus
      if (this.$refs.inputField == document.activeElement) return
      this.focused = false
      this.hidden = true
      this.clearActiveItem()
    },

    inputFocused() {
      if (this.focus && !this.hidden) return
      this.focused = true
      this.hidden = false
      this.searchTerm = ''
      if (this.$refs.inputField) {
        this.$refs.inputField.select()
      }
    },

    inputKeyup(e: any) {
      if (!this.focus || this.hidden) {
        this.focused = true
        this.hidden = false
      }
      if (e.which !== this.keyCodes.enter && e.which !== this.keyCodes.esc) {
        this.hidden = false
      }

      if (!this.preventModelChange(e)) {
        if (this.multiple) {
          this.activePosition = 0
          this.activeItem = this.displayedItems[0] || null
        } else {
          this.activePosition = 1
          this.activeItem = this.displayedItems[1] || null
        }
      }
    },

    preventModelChange(e: any) {
      return [
        this.keyCodes.enter,
        this.keyCodes.esc,
        this.keyCodes.arrowUp,
        this.keyCodes.arrowDown
      ].some(w => e.which === w)
    },

    clearActiveItem() {
      this.activeItem = this.displayedItems[0]
      this.activePosition = 0
      let typeaheadMenuEl = this.$refs.typeaheadMenu
      typeaheadMenuEl.scrollTop = 0
    },

    activateItem(modifier: string) {
      if (this.displayedItems.length) {
        let activePosition
        if (modifier === 'up') {
          activePosition = this.displayedItems[this.activePosition - 1]
            ? this.activePosition - 1
            : this.displayedItems.length - 1
        } else if (modifier === 'down') {
          activePosition = this.displayedItems[this.activePosition + 1]
            ? this.activePosition + 1
            : 0
        }
        this.scrollTypeahead(modifier, activePosition)
        this.activeItem = this.displayedItems[activePosition]
        this.activePosition = activePosition
      }
    },

    scrollTypeahead(modifier: string, activePosition: number) {
      let typeaheadMenuElem = this.$refs.typeaheadMenu
      const maxPossibleScroll = typeaheadMenuElem.scrollHeight - typeaheadMenuElem.clientHeight
      const scrollStep = maxPossibleScroll / (this.displayedItems.length - 1)
      if (modifier === 'up') {
        this.scrollUp(typeaheadMenuElem, activePosition, maxPossibleScroll, scrollStep)
      } else if (modifier === 'down') {
        this.scrollDown(typeaheadMenuElem, activePosition, maxPossibleScroll, scrollStep)
      }
    },

    scrollUp(
      typeaheadMenuElem: Object,
      activePosition: number,
      maxPossibleScroll: number,
      scrollStep: number
    ) {
      activePosition === 0
        ? (typeaheadMenuElem.scrollTop = 0)
        : activePosition === this.displayedItems.length - 1
        ? (typeaheadMenuElem.scrollTop = maxPossibleScroll)
        : (typeaheadMenuElem.scrollTop -= scrollStep)
    },

    scrollDown(
      typeaheadMenuElem: Object,
      activePosition: number,
      maxPossibleScroll: number,
      scrollStep: number
    ) {
      activePosition === this.displayedItems.length - 1
        ? (typeaheadMenuElem.scrollTop = maxPossibleScroll)
        : activePosition === 0
        ? (typeaheadMenuElem.scrollTop = 0)
        : (typeaheadMenuElem.scrollTop += scrollStep)
    },

    hideResults() {
      this.hidden = true
      this.clearActiveItem()
    },

    selectItem(item: any) {
      this.hidden = true
      this.focused = false

      if (item) {
        if (this.doesNotRequirePromptOnAdd(item) || this.promptOnAddAccepted(item)) {
          this.handleSelectItem(item)
        }
      } else {
        this.handleCreateNewItem()
      }

      if (this.$refs.inputField) {
        this.$refs.inputField.blur()
        if (this.multiple) {
          this.$refs.inputField.focus()
        }
      }
    },

    handleSelectItem(item: any) {
      if (this.multiple) {
        this.selectItemMultiple(item)
      } else {
        this.selectItemSingle(item)
      }
      this.$emit('selected', item)
    },

    handleCreateNewItem() {
      if (this.model) {
        if (this.multiple) {
          Promise.resolve(this.createHandler(this.model)).then(() => {
            this.model = ''
          })
        } else {
          Promise.resolve(this.createHandler(this.model)).then(newItem => {
            if (newItem && newItem.name) {
              this.model = this.singleItemFormatter(newItem)
              this.selectHandler(newItem)
            }
          })
        }
      }
    },

    selectItemMultiple(item: any) {
      this.selectHandler(item)
      this.model = ''
    },

    selectItemSingle(item: any) {
      this.model = this.singleItemFormatter(item)
      this.selectedItem = item
      if (item.id === this.noSelectionItem.id) {
        this.model = ''
        this.removeHandler()
      } else {
        this.selectHandler(item)
      }
    },

    removeItem(item: Item) {
      if (this.doesNotRequirePromptOnRemove(item) || this.promptOnRemoveAccepted(item)) {
        this.removeHandler(item)
        this.clearActiveItem()
        if (this.$refs.inputField) {
          this.$refs.inputField.focus()
          this.$refs.inputField.blur()
        }
      }
    },

    removeLastItem() {
      if (this.model === '' && this.multiple && this.currentItems && this.currentItems.length > 0) {
        let lastItem = this.currentItems[this.currentItems.length - 1]
        this.removeItem(lastItem)
        this.$refs.inputField.focus()
      }
    },

    orderItemsByModel() {
      return _.uniq(this.filteredItems.concat(this.items))
    },

    doesNotRequirePromptOnAdd(item: any) {
      return !item.promptOnAdd
    },

    promptOnAddAccepted(item: any) {
      const promptMsg = item.promptOnAddMsg
        ? item.promptOnAddMsg
        : this.$i18n.t('ADD_ITEM_CONFIRM', { thing: item.name })
      return window.confirm(promptMsg)
    },

    doesNotRequirePromptOnRemove(item: any) {
      return !item.promptOnRemove
    },

    promptOnRemoveAccepted(item: any) {
      const promptMsg = item.promptOnRemoveMsg
        ? item.promptOnRemoveMsg
        : this.$i18n.t('REMOVE_ITEM_CONFIRM', { thing: item.name })
      return window.confirm(promptMsg)
    },
    clearModel() {
      this.model = ''
    }
  },

  watch: {
    model: function(m) {
      this.searchTerm = m
    },
    items: {
      handler() {
        this.clearActiveItem()
      },
      deep: true
    },
    currentItems: function(newItems) {
      if (!this.multiple && newItems && newItems.length > 0) this.selectItemSingle(newItems[0])
      else this.model = ''
    },
    currentItem: {
      handler(newItem) {
        this.model =
          (this.singleItemFormatter && !_.isString(newItem)
            ? this.singleItemFormatter(newItem)
            : newItem) || ''
        this.selectedItem = _.isString(newItem) ? { id: newItem, name: newItem } : newItem
      },
      deep: true
    }
  }
}
</script>
